import {
  useMemo,
  useState,
  type ComponentType,
} from "react";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import { Link } from "react-router";
import { Avatar } from "@/components/common/Avatar";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { KpiBadge } from "@/components/design/KpiBadge";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { avatarInitials, AVATAR_PLACEHOLDER_STYLE } from "@/lib/avatar";
import { elementDisplayName, elementNameId } from "@/lib/elements";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useScrollAnchor } from "@/lib/use-scroll-anchor";
import {
  CriterionAiReason,
  CriterionKindHeading,
  CriterionRequirementFilter,
  CriterionRequirementGlyph,
  CriterionStatusDot,
  type CriterionRequirementFilterValue,
} from "./CriterionDrilldown";
import { LockClosedIcon, LockOpenIcon } from "./lock-icons";
import { buildRankingAssistantContext } from "./match-result-assistant-context";
import { resolveTeamFit, type TeamFitBasisLike } from "./match-result-team-fit";
import type { AiAssessmentView, TeamResultView } from "./match-result-codec";
import { InfoTooltip } from "@/components/design/InfoTooltip";
import { RankingBandGroups } from "./RankingBandGroups";
import { RankingInsightsPanel } from "./RankingInsightsPanel";
import { RankingRow } from "./RankingRow";
import { RankingThresholdSlider } from "./RankingThresholdSlider";
import { RankingTopFive } from "./RankingTopFive";
import {
  buildCandidateRanking,
  buildPowerFit,
  buildRadiusInsight,
  buildRankingInsights,
  criterionKindId,
  criterionKindOrder,
  constraintSummary,
  DEFAULT_RANK_THRESHOLDS,
  isLenientLevelMatch,
  labelForCondition,
  type RankCluster,
  type RankSortBy,
  type ResultPerson,
  type RankThresholds,
} from "./match-result-derive";
import type { RequirementLeniency } from "./design-codec";
import { RadiusInsightMap } from "./RadiusInsightMap";
import {
  formatConditionActual,
  useConditionLabelFormatters,
  useConditionLabelLookup,
} from "./result-panel-labels";

interface CandidatePowerFitDetailProps {
  person: ResultPerson;
  view: TeamResultView;
  elementWeights?: Readonly<Record<string, number>>;
}

// REVIEW steht zwischen C und UNQUALIFIED: fehlende Information ist kein Ausschluss,
// aber auch keine Einordnung ins Band (spark #175).
const RANK_CLUSTERS: RankCluster[] = ["A", "B", "C", "REVIEW", "UNQUALIFIED"];

const criteriaCollator = new Intl.Collator("de", { sensitivity: "base" });

// Bewerbung-Ergebnis: geclustertes Bewerber-Ranking. Der Multi-Range-Slider verschiebt live
// die A/B/C-Schwellen; die Bewertung selbst kommt aus genesis (fulfillment je Kriterium).
export function CandidateRankingPanelContent({
  view,
  persons,
  elementWeights,
  requirementLeniency,
  PowerFitDetail,
  thresholds: thresholdsProp,
  onThresholdsChange,
  sortBy: sortByProp,
  onSortByChange,
  teamFitBasis,
}: {
  view: TeamResultView;
  persons: ResultPerson[];
  teamFitBasis?: TeamFitBasisLike | null;
  // Team-Power-Wuensche (Wire 0-1000 je CreativityElement) aus dem Design; treiben den Power-Fit.
  elementWeights?: Readonly<Record<string, number>>;
  // Anforderungspruefung aus dem Design-Snapshot des angezeigten Results: markiert
  // Kulanz-Treffer (erfuellt ohne belegte Stufe) mit amber statt gruen.
  requirementLeniency?: RequirementLeniency | null;
  PowerFitDetail: ComponentType<CandidatePowerFitDetailProps>;
  // Sichtzustand wahlweise von aussen gefuehrt: der PDF-Export nimmt Schwellen und Sortierachse
  // mit, und die Achse veraendert die A/B/C-Baender selbst (buildCandidateRanking) - ein Export
  // mit Vorgabewerten widerspraeche dem Bildschirm, von dem er ausgeloest wurde. Ungefuehrt
  // (Tests, Einzelverwendungen) behaelt das Panel seinen eigenen Zustand.
  thresholds?: RankThresholds;
  onThresholdsChange?: (next: RankThresholds) => void;
  sortBy?: RankSortBy;
  onSortByChange?: (next: RankSortBy) => void;
}) {
  const intl = useIntl();
  // Drei Cluster-Grenzen als Prozent-State (int vermeidet Float-Drift): "A ab", "B ab",
  // "C ab". Unter C -> unterste Gruppe. Mindestabstand 5, Reihenfolge A > B > C erzwungen,
  // THE A/B/C CUT, held here and moved by the shared slider. Until the spark!285 review this file
  // also owned the whole interaction — three percent states, pointer capture, snapping, clamping and
  // the handle geometry, ~90 lines — while the company panel set the same three numbers with number
  // fields. Both now render RankingThresholdSlider; what stays here is the value it edits.
  const [ownThresholds, setOwnThresholds] = useState<RankThresholds>(DEFAULT_RANK_THRESHOLDS);
  const thresholds = thresholdsProp ?? ownThresholds;
  const setThresholds = onThresholdsChange ?? setOwnThresholds;
  // Power-Fit als zweite, ungemischte Achse (spark #151, @adr SPARK-0030-match-result-scoring-atom-derived@1): gewichtetes Mittel der
  // eigenen Element-Scores. Nur sinnvoll, wenn genesis Powers zurueckgibt.
  const powerFitById = useMemo(
    () => buildPowerFit(view.powers, elementWeights),
    [view.powers, elementWeights],
  );
  const hasPowerFit = powerFitById.size > 0;
  const [ownSortBy, setOwnSortBy] = useState<RankSortBy>("smycles");
  const requestedSortBy = sortByProp ?? ownSortBy;
  const setSortBy = onSortByChange ?? setOwnSortBy;
  // Team-Beitrag statt Team-Durchschnitt: der Grenzbeitrag dieser Person zu T(Bestandsteam), als
  // Anteil des ueberhaupt Erreichbaren. Die Achse selbst liegt in match-result-team-fit, damit
  // Bildschirm und PDF sie nicht auseinanderlaufen lassen koennen.
  const teamFit = useMemo(() => resolveTeamFit(view, requestedSortBy, teamFitBasis),
    [view, requestedSortBy, teamFitBasis]);
  const sortBy = teamFit.sortBy;
  const teamFitById = teamFit.contributionById;
  const teamFitTopById = teamFit.topById;
  const rankingFactSkill = useMemo(() => teamFit.withoutIncumbents(view.factSkill),
    [teamFit, view.factSkill]);
  const ranked = useMemo(
    () => buildCandidateRanking(rankingFactSkill, persons, thresholds, powerFitById, sortBy, teamFitById),
    [rankingFactSkill, persons, thresholds, powerFitById, sortBy, teamFitById],
  );
  const personById = useMemo(() => new Map(persons.map((p) => [p.id, p])), [persons]);
  // Top 5 nach ganzheitlicher AI-Eignung; nur qualifizierte (Muss-Gate bestanden) mit Score.
  const topSuitableCandidates = useMemo(
    () =>
      ranked
        .filter((c) => c.qualification === "QUALIFIED" && c.suitabilityScore != null)
        .sort((a, b) => (b.suitabilityScore ?? 0) - (a.suitabilityScore ?? 0))
        .slice(0, 5),
    [ranked],
  );

  const labelLookup = useConditionLabelLookup();
  const conditionLabelFormatters = useConditionLabelFormatters(intl);
  const assistantConditionLabel = useMemo(
    () => (condition: unknown) =>
      labelForCondition(condition, labelLookup, conditionLabelFormatters),
    [conditionLabelFormatters, labelLookup],
  );
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  useAssistantContext(
    useMemo(
      () =>
        buildRankingAssistantContext({
          ranked,
          persons,
          factSkill: view.factSkill,
          thresholds,
          sortBy,
          formatCondition: assistantConditionLabel,
          focusedPersonIds: expanded,
        }),
      [assistantConditionLabel, expanded, persons, ranked, sortBy, thresholds, view.factSkill],
    ),
  );

  const [expandedAiReasons, setExpandedAiReasons] = useState<Set<string>>(new Set());
  const [criteriaTab, setCriteriaTab] = useState<Record<string, "fulfilled" | "lenient" | "unknown" | "unfulfilled">>({});
  const [criteriaReq, setCriteriaReq] = useState<Record<string, CriterionRequirementFilterValue>>({});
  const [collapsedKinds, setCollapsedKinds] = useState<Set<string>>(new Set());
  // Badges wirken als Tab-Switch: pro Kandidat ist immer nur eine Detail-Ansicht offen
  // (Einschaetzung / Kriterien / Power-Fit). expanded steuert auf/zu, activeView welche Ansicht.
  const [activeView, setActiveView] = useState<Record<string, "smycles" | "criteria" | "power" | "team">>({});
  const anchorScroll = useScrollAnchor();
  // Vor jedem Umschalten die Kandidaten-Zeile als Anker festhalten: die Inhalte
  // darunter aendern ihre Hoehe massiv, und der Browser haelt nur scrollTop in
  // Pixeln fest, nicht die Position der geklickten Zeile. Ohne Kompensation
  // springt die Ansicht (Scroll-Clamp bzw. fehlgeleitetes Scroll-Anchoring).
  const holdCandidateAnchor = (personId: string) => {
    anchorScroll(document.querySelector<HTMLElement>(`[data-person-anchor="${personId}"]`));
  };
  function openTab(personId: string, tab: "smycles" | "criteria" | "power" | "team") {
    holdCandidateAnchor(personId);
    setActiveView((prev) => ({ ...prev, [personId]: tab }));
    setExpanded((prev) => new Set(prev).add(personId));
  }
  function toggle(id: string) {
    holdCandidateAnchor(id);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function areClusterMembersOpen(members: Array<{ personId: string }>) {
    return members.length > 0 && members.every((m) => expanded.has(m.personId));
  }
  function toggleClusterMembers(members: Array<{ personId: string }>) {
    setExpanded((prev) => {
      const next = new Set(prev);
      const close = members.length > 0 && members.every((m) => next.has(m.personId));
      for (const member of members) {
        if (close) next.delete(member.personId);
        else next.add(member.personId);
      }
      return next;
    });
  }
  function toggleAiReason(id: string) {
    setExpandedAiReasons((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const formatActual = (condition: unknown, actual: string | null): string | null =>
    formatConditionActual(condition, actual, labelLookup, intl);

  const clusterLabel = (c: RankCluster) => {
    if (c === "UNQUALIFIED") {
      return intl.formatMessage({ id: "match.result.ranking.unqualified", defaultMessage: "Kriterien nicht erfüllt" });
    }
    if (c === "REVIEW") {
      return intl.formatMessage({ id: "match.result.ranking.review", defaultMessage: "Zu prüfen" });
    }
    return c;
  };

  const insights = buildRankingInsights(rankingFactSkill, ranked);
  const radiusInsight = buildRadiusInsight(ranked);
  // Klick auf eine Radius-Bubble springt zur Kandidaten-Zeile im Ranking. Bewusst per DOM-Anker
  // statt Refs: die Zeilen leben tief in Cluster-Disclosures, ein Ref-Geflecht waere schwerer.
  const scrollToCandidate = (personId: string) => {
    const row = document.querySelector<HTMLElement>(`[data-person-anchor="${personId}"]`);
    if (!row) return;
    row.scrollIntoView({ behavior: "smooth", block: "center" });
    row.animate(
      [
        { backgroundColor: "color-mix(in srgb, var(--color-accent) 10%, transparent)" },
        { backgroundColor: "transparent" },
      ],
      { duration: 1600, easing: "ease-out" },
    );
  };
  // Still used outside the analysis box (the score column), so it stays here rather than moving
  // into RankingInsightsPanel with the rest of the formatters.
  const formatPercent = (value: number | null) =>
    value === null
      ? "–"
      : intl.formatNumber(value, { style: "percent", maximumFractionDigits: 0 });
  // Ohne constraintSummary: die Composition ("exact 1") ist in der Meta-Box Rauschen.
  const insightLabel = (condition: unknown) =>
    labelForCondition(condition, labelLookup, conditionLabelFormatters);
  // null = nicht bewertbar (spark#175). Bewusst kein "0 %": das waere eine Behauptung
  // ueber den Kandidaten, die die Daten nicht hergeben.
  const formatScorePercent = (value: number | null, maximumFractionDigits = 0) =>
    value == null ? "–" : intl.formatNumber(value, { style: "percent", maximumFractionDigits });
  const scoreInfoTitle = intl.formatMessage({
    id: "match.result.ranking.scoreInfoTitle",
    defaultMessage: "Rechenweg",
  });
  const scoreInfoAria = intl.formatMessage({
    id: "match.result.ranking.scoreInfoAria",
    defaultMessage: "Rechenweg anzeigen",
  });
  const renderScoreInfo = (candidate: (typeof ranked)[number]) => (
    <div className="space-y-2">
      <div>
        <FormattedMessage
          id="match.result.ranking.scoreFormula"
          defaultMessage="Score = Durchschnitt der Kann-Kriterien."
        />
      </div>
      {/* KANN-only nach Kategorie zusammengefasst (erfuellt/gesamt); MUSS ist ein Gate,
          in der Notiz unten abgedeckt und wuerde die Summe nicht treffen (@adr SPARK-0030-match-result-scoring-atom-derived@1 §3). */}
      <div className="space-y-1">
        {(() => {
          const groups: { kind: string; order: number; met: number; total: number }[] = [];
          for (const criterion of candidate.criteria.filter((c) => c.requirement !== "MUST")) {
            const kind = criterionKindLabel(criterion.condition, intl) ?? "";
            let g = groups.find((x) => x.kind === kind);
            if (!g) {
              g = { kind, order: criterionKindOrder(criterion.condition), met: 0, total: 0 };
              groups.push(g);
            }
            g.total += 1;
            if ((criterion.fulfillment ?? 0) >= 1) g.met += 1;
          }
          groups.sort((a, b) => a.order - b.order);
          return groups.map((g) => (
            <div key={g.kind} className="flex items-center justify-between gap-3">
              <span className="min-w-0 truncate">{g.kind}</span>
              <span className="shrink-0 tabular-nums">
                {g.met}/{g.total}
              </span>
            </div>
          ));
        })()}
      </div>
      <div className="pt-2 tabular-nums">
        {candidate.score == null ? (
          <FormattedMessage
            id="match.result.ranking.scoreNotAssessable"
            defaultMessage="Kann-Kriterien: nicht bewertbar"
          />
        ) : (
          <FormattedMessage
            id="match.result.ranking.scoreCriteriaValue"
            defaultMessage="Kann-Kriterien: {criteria}"
            values={{ criteria: formatScorePercent(candidate.score) }}
          />
        )}
      </div>
      {/* Belastbarkeit getrennt vom Score: ein Wert aus 2 von 5 Kriterien ist etwas anderes
          als derselbe Wert aus 5 von 5. Nie in den Score multipliziert (@adr SPARK-0030-match-result-scoring-atom-derived@1). */}
      {candidate.kannDefined > candidate.kannTotal ? (
        <div className="tabular-nums text-ink-muted">
          <FormattedMessage
            id="match.result.ranking.scoreCoverage"
            defaultMessage="{assessable} von {defined} Kriterien bewertbar"
            values={{ assessable: candidate.kannTotal, defined: candidate.kannDefined }}
          />
        </div>
      ) : null}
      <div>
        <FormattedMessage
          id="match.result.ranking.scoreMustGate"
          defaultMessage="Muss-Kriterien bleiben zusätzlich ein hartes Gate."
        />
      </div>
    </div>
  );
  const mustInfoTitle = intl.formatMessage({
    id: "match.result.ranking.mustInfoTitle",
    defaultMessage: "Muss-Kriterien",
  });
  const mustInfoAria = intl.formatMessage({
    id: "match.result.ranking.mustInfoAria",
    defaultMessage: "Muss-Kriterien anzeigen",
  });
  const mustMetLabel = intl.formatMessage({
    id: "match.result.ranking.criterionMet",
    defaultMessage: "erfüllt",
  });
  const mustFailedLabel = intl.formatMessage({
    id: "match.result.ranking.criterionFailed",
    defaultMessage: "nicht erfüllt",
  });
  // Nur die MUSS-Kriterien mit Erfuellt-Status; die Kann-Kriterien haben ihren eigenen
  // Rechenweg im Erfuellt-Badge (@adr SPARK-0030-match-result-scoring-atom-derived@1 §3: Muss ist ein Gate, kein Score-Bestandteil).
  const renderMustInfo = (candidate: (typeof ranked)[number]) => {
    const must = candidate.criteria.filter((criterion) => criterion.requirement === "MUST");
    return (
      <div className="space-y-2">
        <div>
          <FormattedMessage
            id="match.result.ranking.mustFormula"
            defaultMessage="Muss-Kriterien sind harte Gates: jedes muss erfüllt sein."
          />
        </div>
        <div className="space-y-1">
          {must.map((criterion, index) => {
            const met = (criterion.fulfillment ?? 0) >= 1;
            return (
              <div key={index} className="flex items-center justify-between gap-3">
                <span className="min-w-0 truncate">{insightLabel(criterion.condition)}</span>
                {/* Glyph plus aria-label tragen die Bedeutung, nicht die Farbe: Rot ist laut
                    UI_CONVENTIONS destruktiven Aktionen vorbehalten. Erfuellt = Accent,
                    verfehlt = zurueckgenommene Ink-Stufe, wie beim Kriterien-Marker unten. */}
                <span
                  aria-label={met ? mustMetLabel : mustFailedLabel}
                  className={`shrink-0 tabular-nums ${met ? "text-ink-accent" : "text-ink-muted"}`}
                >
                  {met ? "✓" : "✗"}
                </span>
              </div>
            );
          })}
        </div>
        <div className="pt-1 tabular-nums">
          <FormattedMessage
            id="match.result.ranking.mustMetSummary"
            defaultMessage="Erfüllt: {met}/{total}"
            values={{ met: candidate.mustMet, total: candidate.mustTotal }}
          />
        </div>
      </div>
    );
  };
  const recommendationContent =
    insights.recommendation?.type === "must" ? (
      <FormattedMessage
        id="match.result.insights.recommendMust"
        defaultMessage="{criterion} prüfen oder lockern; dieses Muss-Kriterium schließt {failed}/{total} Kandidaten aus."
        values={{
          criterion: insightLabel(insights.recommendation.criterion.condition),
          failed: insights.recommendation.criterion.failedCount,
          total: insights.recommendation.criterion.total,
        }}
      />
    ) : insights.recommendation?.type === "data" ? (
      <FormattedMessage
        id="match.result.insights.recommendData"
        defaultMessage="Profilwerte für {criterion} nachpflegen; {missing}/{total} Nachweise fehlen."
        values={{
          criterion: insightLabel(insights.recommendation.criterion.condition),
          missing: insights.recommendation.criterion.missingActualCount,
          total: insights.recommendation.criterion.expectedActualCount,
        }}
      />
    ) : insights.recommendation?.type === "ai" ? (
      <FormattedMessage
        id="match.result.insights.recommendAi"
        defaultMessage="{count} AI Reasons bei {candidates} Kandidaten manuell prüfen."
        values={{ count: insights.recommendation.reasonCount, candidates: insights.recommendation.candidateCount }}
      />
    ) : insights.recommendation?.type === "bottleneck" ? (
      <FormattedMessage
        id="match.result.insights.recommendBottleneck"
        defaultMessage="{criterion} ist der stärkste Engpass; Ø Erfüllung {average}."
        values={{
          criterion: insightLabel(insights.recommendation.criterion.condition),
          average: formatPercent(insights.recommendation.criterion.averageFulfillment),
        }}
      />
    ) : (
      <FormattedMessage id="match.result.insights.recommendNone" defaultMessage="Keine unmittelbare Anpassung nötig." />
    );

  return (
    <div className="space-y-3">
      {/* MATCH ANALYSIS, drawn by the shared panel (review of spark!285, point 3). It was ~250
          lines of JSX and derivations right here, and the company ranking had none at all; Kevin
          decided on 2026-09-09 that a company result shows the same three surfaces a person result
          does. What stays on this side is what is person-shaped: how a criterion is named, how one
          recorded value reads, and the radius card. */}
      {insights.pool.total > 0 && (
        <RankingInsightsPanel
          insights={insights}
          labelForCondition={insightLabel}
          subjectLabel={intl.formatMessage({
            id: "match.result.insights.subjectCandidates",
            defaultMessage: "Kandidaten",
          })}
          formatActual={formatActual}
          recommendationContent={recommendationContent}
          // The person side's own note: person-detail criteria carry no analysis distribution, and
          // this is where the reader asks why. It lives here rather than in the shared box because
          // it is a statement about THIS side's criterion kinds.
          fulfilledNote={
            <InfoTooltip
              ariaLabel={intl.formatMessage({
                id: "match.result.insights.personDetailsInfoAria",
                defaultMessage: "Hinweis zu Personen-Details anzeigen",
              })}
              title={
                <FormattedMessage
                  id="match.result.insights.personDetailsInfoTitle"
                  defaultMessage="Personen-Details"
                />
              }
              placement="bottom"
            >
              <FormattedMessage
                id="match.result.insights.personDetailsInfoBody"
                defaultMessage="Personen-Details werden nicht als Analyse-Verteilung dargestellt. Sie bleiben im Kandidaten-Detail sichtbar."
              />
            </InfoTooltip>
          }
          radiusSlot={
            radiusInsight ? (
              <RadiusInsightMap insight={radiusInsight} onSelectPerson={scrollToCandidate} />
            ) : null
          }
        />
      )}
      {(view.factSkill?.teamMembers.length ?? 0) > 0 && (
        <RankingTopFive
          testId="suitability-top-five"
          entryTestId="suitability-candidate"
          basis={
            <FormattedMessage
              id="match.result.suitability.basis"
              defaultMessage="AI-Gesamteinschätzung"
            />
          }
          emptyHint={
            <FormattedMessage
              id="match.result.suitability.empty"
              defaultMessage="Noch keine AI-Eignungsscores vorhanden. Bitte den Match erneut ausführen."
            />
          }
          entries={topSuitableCandidates.map((candidate) => {
            const person = personById.get(candidate.personId);
            return {
              key: candidate.personId,
              to: `/p/${candidate.personId}`,
              openLabel: intl.formatMessage(
                {
                  id: "match.result.suitability.openProfile",
                  defaultMessage: "{name} – Profil öffnen",
                },
                { name: candidate.personName },
              ),
              name: candidate.personName,
              subtitle: person?.profession ?? null,
              score: candidate.suitabilityScore ?? 0,
              scoreLabel: intl.formatMessage(
                {
                  id: "match.result.suitability.scoreLabel",
                  defaultMessage: "AI-Eignung {score} %",
                },
                { score: Math.round((candidate.suitabilityScore ?? 0) * 100) },
              ),
              image: (
                <PersonTile
                  name={candidate.personName}
                  avatarContent={person?.avatarContent}
                  avatarMime={person?.avatarMime}
                />
              ),
            };
          })}
        />
      )}
    <div className="box bg-surface p-5">
      <BoxHeader title={<FormattedMessage id="match.result.rankingHeader" defaultMessage="Ranking" />} />
      <RankingThresholdSlider thresholds={thresholds} onChange={setThresholds} />
      {hasPowerFit || teamFitById ? (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="smycles-field-label text-ink-muted">
            <FormattedMessage id="match.result.ranking.sortBy" defaultMessage="Sortieren nach" />
          </span>
          <SegmentedControl<RankSortBy>
            className="max-w-full"
            size="md"
            value={sortBy}
            onChange={setSortBy}
            options={[
              {
                value: "smycles",
                label: <FormattedMessage id="match.result.ranking.sortSmycles" defaultMessage="Smycles" />,
              },
              {
                value: "requirements",
                label: <FormattedMessage id="match.result.ranking.sortRequirements" defaultMessage="Anforderungs-Fit" />,
              },
              {
                value: "power",
                label: <FormattedMessage id="match.result.ranking.sortPower" defaultMessage="Power-Fit" />,
              },
              // Ohne Bestandsteam bleibt die Achse sichtbar, aber inaktiv: sonst fehlt sie
              // kommentarlos und niemand erfaehrt, dass ueberhaupt etwas einzurichten waere.
              {
                value: "team" as const,
                disabled: !teamFitById,
                label: teamFitById
                  ? <FormattedMessage id="match.teamFit.resultTitle" />
                  : <span title={intl.formatMessage({ id: "match.teamFit.sortUnavailable" })}>
                      <FormattedMessage id="match.teamFit.resultTitle" />
                    </span>,
              },
            ]}
          />
        </div>
      ) : null}
      {sortBy === "team" ? <p className="mb-3 smycles-field-meta text-ink-muted"><FormattedMessage id="match.teamFit.sortHint" /></p> : null}
      {/* One collapsible card per band, in the shared shell (RankingBandGroups) — the company
          ranking renders the same one, so the two lists cannot drift apart in rhythm or in the way
          an empty band explains itself. */}
      <RankingBandGroups
        idPrefix="ranking-cluster"
        groups={RANK_CLUSTERS.map((cluster) => {
          const members = ranked.filter((r) => r.cluster === cluster);
          const clusterOpen = areClusterMembersOpen(members);
          return {
            band: cluster,
            title: clusterLabel(cluster),
            count: members.length,
            emptyHint:
              cluster === "UNQUALIFIED" ? (
                // Leerer Zustand erklaert die zwei Wege in diese Gruppe.
                <FormattedMessage
                  id="match.result.ranking.noMustHint"
                  defaultMessage="Hier landen Bewerber, die ein Muss-Kriterium (Schloss) verfehlen oder deren Score unter der C-Schwelle liegt."
                />
              ) : cluster === "REVIEW" ? (
                <FormattedMessage
                  id="match.result.ranking.reviewHint"
                  defaultMessage="Hier landen Bewerber, bei denen ein Kriterium nicht bewertet werden konnte, etwa eine Fachkompetenz ohne eingestufte Stufe. Sie sind nicht durchgefallen, sondern brauchen einen Blick."
                />
              ) : undefined,
            headerAction: (
              <Button
                variant="chip"
                size="sm"
                className="shrink-0"
                onClick={() => toggleClusterMembers(members)}
              >
                {clusterOpen ? (
                  <FormattedMessage id="match.result.ranking.collapseAll" defaultMessage="Alle einklappen" />
                ) : (
                  <FormattedMessage id="match.result.ranking.expandAll" defaultMessage="Alle aufklappen" />
                )}
              </Button>
            ),
            children: (
                <ul>
                  {members.map((m) => {
                    const person = personById.get(m.personId);
                    const open = expanded.has(m.personId);
                    // Badges als Tab-Switch: nur die aktive Ansicht wird gezeigt. Default =
                    // Einschaetzung, wenn ein Smycles-Score existiert, sonst Kriterien.
                    const tab = activeView[m.personId] ?? (m.suitabilityScore != null ? "smycles" : "criteria");
                    const activeReq = criteriaReq[m.personId] ?? "all";
                    const smyclesActive = open && tab === "smycles";
                    const mustActive = open && tab === "criteria" && activeReq === "must";
                    const kannActive = open && tab === "criteria" && activeReq === "kann";
                    const powerActive = open && tab === "power";
                    // Zweite Achse: Power-Fit als eigenes, ungemischtes Prozent-Badge neben dem
                    // Anforderungs-Fit (spark #151). Nur wenn Powers vorliegen.
                    const powerFitLabel = m.powerFit != null ? `${Math.round(m.powerFit)} %` : null;
                    // Muss-Erfuellung getrennt vom KANN-Score (spark #151): ein qualifizierter
                    // Kandidat mit schwachem KANN (z.B. "0 %") soll nicht als Versager gelesen
                    // werden. Der Muss-Chip bleibt immer sichtbar, ohne Muss-Kriterien ausgegraut,
                    // damit erkennbar ist, dass es keine gibt (geschlossenes Schloss = Muss).
                    const hasMust = m.mustTotal > 0;
                    const mustPillTitle = hasMust
                      ? intl.formatMessage({ id: "match.result.ranking.mustTitle", defaultMessage: "Muss-Kriterien" })
                      : intl.formatMessage({ id: "match.result.ranking.mustNone", defaultMessage: "Keine Muss-Kriterien" });
                    // KI-Schalter des Matches (MatchData.aiEnabled): der Editor schreibt ihn als
                    // aiMode auf die KI-faehigen Conditions. Aus -> keine AI-Einschaetzungs-Region,
                    // auch keine leere Spalte reservieren.
                    const aiEnabled = m.criteria.some(
                      (rc) => (rc.condition as { aiMode?: string } | null)?.aiMode === "FULL_AI",
                    );
                    return (
                      <RankingRow
                        key={m.personId}
                        id={`ranking-candidate-${m.personId}`}
                        open={open}
                        onToggle={() => toggle(m.personId)}
                        anchorAttribute={{ "data-person-anchor": m.personId }}
                        name={m.personName}
                        toggleLabel={intl.formatMessage(
                          { id: "match.result.ranking.toggleEvidence", defaultMessage: "Nachweise für {name} anzeigen" },
                          { name: m.personName },
                        )}
                        bubble={
                          <Link
                            to={`/p/${m.personId}`}
                            onClick={(e) => e.stopPropagation()}
                            title={intl.formatMessage({
                              id: "match.result.ranking.openProfile",
                              defaultMessage: "Profil öffnen",
                            })}
                            className="block rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ink-accent-ui"
                          >
                            <Avatar
                              person={{
                                name: m.personName,
                                avatarContent: person?.avatarContent,
                                avatarMime: person?.avatarMime,
                              }}
                              size="lg"
                            />
                          </Link>
                        }
                        badges={
                          <>
                            {m.suitabilityScore != null ? (
                              <KpiBadge
                                active={smyclesActive}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openTab(m.personId, "smycles");
                                }}
                              >
                                <FormattedMessage id="match.result.ranking.kpiSmycles" defaultMessage="Smycles Score" />
                                <span>{Math.round(m.suitabilityScore * 100)} %</span>
                              </KpiBadge>
                            ) : null}
                            <KpiBadge
                              tone={hasMust ? "neutral" : "muted"}
                              active={mustActive}
                              title={mustPillTitle}
                              onClick={(e) => {
                                // Tab "Kriterien", Filter auf Muss (geschlossenes Schloss). Auch ohne
                                // Muss-Kriterien klickbar; die Liste bleibt dann leer, was das
                                // ausgegraute Badge bestaetigt.
                                e.stopPropagation();
                                setCriteriaReq((prev) => ({ ...prev, [m.personId]: "must" }));
                                // Auf den Tab schalten, der etwas zu zeigen hat: sobald ein Muss
                                // erfuellt ist, ist "Erfuellt" nicht leer. Nur wenn kein einziges
                                // erfuellt ist, waere dieser Tab leer, dann direkt "Nicht erfuellt".
                                setCriteriaTab((prev) => ({
                                  ...prev,
                                  [m.personId]: m.mustMet > 0 ? "fulfilled" : "unfulfilled",
                                }));
                                openTab(m.personId, "criteria");
                              }}
                              info={
                                hasMust
                                  ? {
                                      title: mustInfoTitle,
                                      ariaLabel: mustInfoAria,
                                      width: "w-[20rem] max-w-[20rem]",
                                      content: renderMustInfo(m),
                                    }
                                  : undefined
                              }
                            >
                              <LockClosedIcon />
                              <FormattedMessage id="match.result.ranking.must" defaultMessage="Muss" />
                              <span>
                                {m.mustMet}/{m.mustTotal}
                              </span>
                            </KpiBadge>
                            <KpiBadge
                              active={kannActive}
                              title={intl.formatMessage({
                                id: "match.result.ranking.kannTitle",
                                defaultMessage: "Kann-Kriterien",
                              })}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCriteriaReq((prev) => ({ ...prev, [m.personId]: "kann" }));
                                setCriteriaTab((prev) => ({ ...prev, [m.personId]: "fulfilled" }));
                                openTab(m.personId, "criteria");
                              }}
                              info={{ title: scoreInfoTitle, ariaLabel: scoreInfoAria, content: renderScoreInfo(m) }}
                            >
                              <LockOpenIcon />
                              <FormattedMessage id="match.result.ranking.optional" defaultMessage="Kann" />
                              {/* Summe der Erfuellungsgrade, nicht die Anzahl voll erfuellter
                                  Kriterien: 2,4/3 ist genau der Score (80 %), nach dem
                                  "Anforderungs-Fit" sortiert und geclustert wird. */}
                              {/* Kein "0/0", wenn zwar Kann-Kriterien definiert, aber keines bewertbar
                                  ist: das läse sich wie "alles verfehlt" (spark#175). */}
                              <span>
                                {m.kannTotal === 0 && m.kannDefined > 0
                                  ? "–"
                                  : `${intl.formatNumber(m.kannSum, { maximumFractionDigits: 1 })}/${m.kannTotal}`}
                              </span>
                            </KpiBadge>
                            {powerFitLabel ? (
                              <KpiBadge
                                active={powerActive}
                                title={intl.formatMessage({
                                  id: "match.result.ranking.powerFitTooltip",
                                  defaultMessage: "Power-Fit: gewichtetes Mittel der eigenen Power-Werte dieser Person.",
                                })}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openTab(m.personId, "power");
                                }}
                              >
                                <FormattedMessage id="match.result.ranking.kpiPowerFit" defaultMessage="Power-Fit" />
                                <span>{powerFitLabel}</span>
                              </KpiBadge>
                            ) : null}
                            {view.recruitingTeamFit ? <KpiBadge active={open && tab === "team"}
                              title={intl.formatMessage({ id: "match.teamFit.badgeTooltip" })}
                              onClick={e => { e.stopPropagation(); openTab(m.personId, "team"); }}>
                              <FormattedMessage id="match.teamFit.resultTitle" />
                              <span>{m.teamFit == null ? "–" : `${intl.formatNumber(m.teamFit, { maximumFractionDigits: 1 })} %`}</span>
                            </KpiBadge> : null}
                            {/* Im Prüf-Cluster muss ohne Aufklappen erkennbar sein, WORAN es hakt:
                                ein unbewertbares Muss ist etwas anderes als ein nicht bewertbarer
                                Kann-Score (spark#175). Beides kann gleichzeitig zutreffen. */}
                            {m.cluster === "REVIEW" ? (
                              <span
                                className="rounded-full bg-ink/[0.06] px-2 py-0.5 text-smycles-micro font-semibold uppercase leading-none tracking-[0.12em] text-ink-muted ring-1 ring-ink/10"
                                data-testid={`review-reason-${m.personId}`}
                              >
                                {m.qualification === "REVIEW_REQUIRED" && m.score === null ? (
                                  <FormattedMessage
                                    id="match.result.ranking.reviewReasonBoth"
                                    defaultMessage="Muss und Kann nicht bewertbar"
                                  />
                                ) : m.qualification === "REVIEW_REQUIRED" ? (
                                  <FormattedMessage
                                    id="match.result.ranking.reviewReasonMust"
                                    defaultMessage="Muss-Kriterium nicht bewertbar"
                                  />
                                ) : (
                                  <FormattedMessage
                                    id="match.result.ranking.reviewReasonKann"
                                    defaultMessage="Kann-Kriterien nicht bewertbar"
                                  />
                                )}
                              </span>
                            ) : null}
                          </>
                        }
                      >
                            {/* Side-by-side erst ab @7xl: die Spalten-Minima (7rem Einzug + 26rem
                                Anforderung + 20rem Nachweis + 18rem Einschaetzung + Gap) brauchen
                                ~72rem; ab @4xl lief das Grid rechts aus der Karte und der
                                Einschaetzungstext wurde an der Kante abgeschnitten. */}
                            <div className="pb-2.5 pl-1 pr-1 pt-4">
                              <div className="pl-3">
                              {tab === "criteria" ? (
                              <div className="min-w-0">
                              {m.criteria.length === 0 ? (
                                <div className="text-smycles-label text-ink-muted">
                                  <FormattedMessage
                                    id="match.result.ranking.noCriteria"
                                    defaultMessage="Keine Kriterien definiert"
                                  />
                                </div>
                              ) : (
                                <div className="max-w-5xl">
                                  {(() => {
                                    const withMeta = m.criteria.map((rc, originalIndex) => {
                                      const met = rc.fulfillment != null && rc.fulfillment >= 1;
                                      const unknown = !rc.assessable;
                                      return {
                                        rc,
                                        originalIndex,
                                        met,
                                        unknown,
                                        lenient: isLenientLevelMatch(
                                          rc.condition, rc.requirement, rc.aiReason, met, requirementLeniency,
                                        ),
                                      };
                                    });
                                    const fulfilledCount = withMeta.filter((o) => o.met).length;
                                    const unknownCount = withMeta.filter((o) => o.unknown).length;
                                    const lenientCount = withMeta.filter((o) => o.lenient).length;
                                    const unfulfilledCount = withMeta.filter((o) => !o.met && !o.unknown).length;
                                    const tab = criteriaTab[m.personId] ?? "fulfilled";
                                    const reqFilter = criteriaReq[m.personId] ?? "all";
                                    const inTab = withMeta.filter((o) =>
                                      tab === "lenient" ? o.lenient : tab === "unknown" ? o.unknown : tab === "fulfilled" ? o.met : !o.met && !o.unknown,
                                    );
                                    const mustCount = inTab.filter((o) => o.rc.requirement === "MUST").length;
                                    const kannCount = inTab.length - mustCount;
                                    const shown = inTab
                                      .filter((o) =>
                                        reqFilter === "all"
                                          ? true
                                          : reqFilter === "must"
                                            ? o.rc.requirement === "MUST"
                                            : o.rc.requirement !== "MUST",
                                      )
                                      .sort((left, right) => {
                                        const ll = labelForCondition(left.rc.condition, labelLookup, conditionLabelFormatters);
                                        const rl = labelForCondition(right.rc.condition, labelLookup, conditionLabelFormatters);
                                        return criteriaCollator.compare(ll, rl) || left.originalIndex - right.originalIndex;
                                      });
                                    const groups: { kind: string; order: number; items: typeof shown }[] = [];
                                    for (const item of shown) {
                                      const kind = criterionKindLabel(item.rc.condition, intl) ?? "";
                                      let g = groups.find((x) => x.kind === kind);
                                      if (!g) {
                                        g = { kind, order: criterionKindOrder(item.rc.condition), items: [] };
                                        groups.push(g);
                                      }
                                      g.items.push(item);
                                    }
                                    groups.sort((a, b) => a.order - b.order);
                                    return (
                                      <>
                                        <div className="flex flex-wrap items-center gap-3 pb-3">
                                          <SegmentedControl<"fulfilled" | "lenient" | "unknown" | "unfulfilled">
                                            size="kpi"
                                            value={tab}
                                            onChange={(v) => {
                                              holdCandidateAnchor(m.personId);
                                              setCriteriaTab((prev) => ({ ...prev, [m.personId]: v }));
                                            }}
                                            options={[
                                              {
                                                value: "fulfilled",
                                                label: (
                                                  <FormattedMessage
                                                    id="match.result.ranking.fulfilledTab"
                                                    defaultMessage="Erfüllt ({count})"
                                                    values={{ count: fulfilledCount }}
                                                  />
                                                ),
                                              },
                                              // Nur sichtbar, wenn es Kulanz-Treffer gibt (bei "Beides streng"
                                              // kann es keine geben, ein leerer Tab waere Rauschen).
                                              ...(lenientCount > 0
                                                ? [
                                                    {
                                                      value: "lenient" as const,
                                                      label: (
                                                        // Bewusst ohne amber Punkt: Chromatik bleibt den Status-Dots
                                                        // in den Zeilen vorbehalten (ive-design-principles: monochrome
                                                        // Controls), die Bruecke traegt das geteilte Wort "Kulant".
                                                        <span
                                                          title={intl.formatMessage({
                                                            id: "match.result.ranking.lenientMatchHint",
                                                            defaultMessage:
                                                              "Erfüllt per kulanter Anforderungsprüfung: Fachkompetenz vorhanden, geforderte Stufe nicht belegt.",
                                                          })}
                                                        >
                                                          <FormattedMessage
                                                            id="match.result.ranking.lenientTab"
                                                            defaultMessage="Kulant erfüllt ({count})"
                                                            values={{ count: lenientCount }}
                                                          />
                                                        </span>
                                                      ),
                                                    },
                                                  ]
                                                : []),
                                              {
                                                value: "unfulfilled",
                                                label: (
                                                  <FormattedMessage
                                                    id="match.result.ranking.unfulfilledTab"
                                                    defaultMessage="Nicht erfüllt ({count})"
                                                    values={{ count: unfulfilledCount }}
                                                  />
                                                ),
                                              },
                                              // Nur anbieten, wenn es etwas zu prüfen gibt: ein leerer dritter
                                              // Tab wäre bei vollständigen Profilen reines Rauschen.
                                              ...(unknownCount > 0
                                                ? [
                                                    {
                                                      value: "unknown" as const,
                                                      label: (
                                                        <FormattedMessage
                                                          id="match.result.ranking.unknownTab"
                                                          defaultMessage="Zu prüfen ({count})"
                                                          values={{ count: unknownCount }}
                                                        />
                                                      ),
                                                    },
                                                  ]
                                                : []),
                                            ]}
                                          />
                                          {/* Shared with the company ranking since 2026-09-09
                                              (CriterionDrilldown): same control, same two lock
                                              glyphs, same accent fill on the active Muss segment.
                                              `order-first` stays here because the ORDER is this
                                              panel's layout decision - the outcome tabs are the
                                              first child in the DOM and this control is pulled
                                              ahead of them visually. */}
                                          <CriterionRequirementFilter
                                            className="order-first"
                                            value={reqFilter}
                                            mustCount={mustCount}
                                            kannCount={kannCount}
                                            onChange={(v) => {
                                              holdCandidateAnchor(m.personId);
                                              setCriteriaReq((prev) => ({ ...prev, [m.personId]: v }));
                                            }}
                                          />
                                        </div>
                                        {groups.length === 0 ? (
                                          // Ive-Loop Schritt 4: Leerzustand erklaert die Ursache (aktive
                                          // Filter-Kombination) und bietet genau einen Ausweg an, statt als
                                          // Fluesterzeile unterzugehen ("wo sind meine Ergebnisse?").
                                          (() => {
                                            const tabNames = {
                                              fulfilled: intl.formatMessage({ id: "match.result.ranking.tabNameFulfilled", defaultMessage: "Erfüllt" }),
                                              lenient: intl.formatMessage({ id: "match.result.ranking.tabNameLenient", defaultMessage: "Kulant erfüllt" }),
                                              unknown: intl.formatMessage({ id: "match.result.ranking.tabNameUnknown", defaultMessage: "Zu prüfen" }),
                                              unfulfilled: intl.formatMessage({ id: "match.result.ranking.tabNameUnfulfilled", defaultMessage: "Nicht erfüllt" }),
                                            } as const;
                                            const reqCulprit = reqFilter !== "all" && inTab.length > 0;
                                            const fallbackTab =
                                              fulfilledCount > 0 ? ("fulfilled" as const)
                                              : lenientCount > 0 ? ("lenient" as const)
                                              : ("unfulfilled" as const);
                                            return (
                                              // Hinweis-Block mit einmaligem hint-flash; key = Ursache, damit der
                                              // Flash nur bei einer NEUEN leeren Filterkombination erneut laeuft.
                                              // Bewusst ohne die border-l-2 des Hinweis-Primitives: die Kandidaten-
                                              // Sektion bringt bereits eine linke Linie mit, zwei Linien nebeneinander
                                              // lesen sich als Layoutfehler. Aufmerksamkeit = hint-flash + bg-accent/5.
                                              <div
                                                key={`${tab}:${reqFilter}`}
                                                className="hint-flash my-2 bg-accent/5 px-4 py-3"
                                                data-testid={`criteria-empty-${m.personId}`}
                                              >
                                                <div className="text-smycles-micro tracking-[0.2em] text-ink-accent">
                                                  <FormattedMessage
                                                    id="match.result.ranking.emptyHintLabel"
                                                    defaultMessage="Hinweis"
                                                  />
                                                </div>
                                                <div className="mt-1 smycles-field-value flex flex-wrap items-center gap-x-4 gap-y-1 text-ink">
                                                <span>
                                                  {reqCulprit ? (
                                                    <FormattedMessage
                                                      id="match.result.ranking.emptyFilteredTab"
                                                      defaultMessage="Keine {requirement}-Kriterien unter „{tab}“."
                                                      values={{
                                                        requirement:
                                                          reqFilter === "must"
                                                            ? intl.formatMessage({ id: "match.result.ranking.must", defaultMessage: "Muss" })
                                                            : intl.formatMessage({ id: "match.result.ranking.optional", defaultMessage: "Kann" }),
                                                        tab: tabNames[tab],
                                                      }}
                                                    />
                                                  ) : (
                                                    <FormattedMessage
                                                      id="match.result.ranking.emptyTab"
                                                      defaultMessage="Unter „{tab}“ gibt es keine Kriterien."
                                                      values={{ tab: tabNames[tab] }}
                                                    />
                                                  )}
                                                </span>
                                                {reqCulprit ? (
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                      holdCandidateAnchor(m.personId);
                                                      setCriteriaReq((prev) => ({ ...prev, [m.personId]: "all" }));
                                                    }}
                                                  >
                                                    <FormattedMessage
                                                      id="match.result.ranking.emptyShowAll"
                                                      defaultMessage="Alle Kriterien zeigen"
                                                    />
                                                  </Button>
                                                ) : (
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                      holdCandidateAnchor(m.personId);
                                                      setCriteriaTab((prev) => ({ ...prev, [m.personId]: fallbackTab }));
                                                      setCriteriaReq((prev) => ({ ...prev, [m.personId]: "all" }));
                                                    }}
                                                  >
                                                    <FormattedMessage
                                                      id="match.result.ranking.emptySwitchTab"
                                                      defaultMessage="Zu „{tab}“ wechseln"
                                                      values={{ tab: tabNames[fallbackTab] }}
                                                    />
                                                  </Button>
                                                )}
                                                </div>
                                              </div>
                                            );
                                          })()
                                        ) : (
                                          groups.map((group) => {
                                            const kindKey = `${m.personId}:${group.kind}`;
                                            const kindOpen = !collapsedKinds.has(kindKey);
                                            // Chromium occasionally keeps a stale two-column balance after a
                                            // tall AI reason in this CSS multi-column block is closed. A key
                                            // derived from the open reasons remounts only this criterion group,
                                            // forcing a fresh balance back to the responsive 2/3-column layout.
                                            const columnBalanceKey = group.items
                                              .filter((item) => Boolean(item.rc.aiReason?.trim()))
                                              .map(({ originalIndex }) => {
                                                const reasonKey = `${m.personId}:${originalIndex}`;
                                                return `${originalIndex}:${expandedAiReasons.has(reasonKey) ? "open" : "closed"}`;
                                              })
                                              .join("|");
                                            return (
                                            <div key={group.kind} className="mb-4">
                                              {/* Shared heading (CriterionDrilldown). The empty
                                                  kind is still the one that gets NO heading: a
                                                  condition this build has no category for would
                                                  otherwise be filed under a blank one. */}
                                              {group.kind ? (
                                                <CriterionKindHeading
                                                  label={group.kind}
                                                  count={group.items.length}
                                                  open={kindOpen}
                                                  onToggle={() => {
                                                    holdCandidateAnchor(m.personId);
                                                    setCollapsedKinds((prev) => {
                                                      const next = new Set(prev);
                                                      if (next.has(kindKey)) next.delete(kindKey);
                                                      else next.add(kindKey);
                                                      return next;
                                                    });
                                                  }}
                                                />
                                              ) : null}
                                              {kindOpen ? (
                                              <div
                                                key={columnBalanceKey}
                                                data-requirement-columns={`${m.personId}:${group.kind}`}
                                                className="gap-x-6 @3xl:columns-2 @7xl:columns-3"
                                              >
                                              {group.items.map(({ rc, originalIndex, met, unknown, lenient: lenientMatch }) => {
                                                const fulfillment = rc.fulfillment;
                                                const assessable = rc.assessable;
                                                const partial = fulfillment != null && fulfillment > 0 && fulfillment < 1;
                                                const label = labelForCondition(rc.condition, labelLookup, conditionLabelFormatters);
                                                const constraint = visibleConstraintSummary(constraintSummary(rc.condition));
                                                const actualText = formatActual(rc.condition, rc.actual);
                                                const pct = partial ? `${Math.round(fulfillment * 100)} %` : null;
                                                // Nicht bewertbar heißt genau eins: das Kriterium ist vorhanden, aber
                                                // nicht eingestuft. Nie "nicht vorhanden" (spark#175).
                                                const rightValue = !assessable
                                                  ? intl.formatMessage({
                                                      id: "match.result.ranking.notRated",
                                                      defaultMessage: "Vorhanden · Stufe nicht bewertet",
                                                    })
                                                  : actualText
                                                    ? pct
                                                      ? `${actualText} (${pct})`
                                                      : actualText
                                                    : pct;
                                                const aiReason = rc.aiReason?.trim() || null;
                                                const isMust = rc.requirement === "MUST";
                                                const requirementLabel = isMust
                                                  ? intl.formatMessage({ id: "match.result.ranking.must", defaultMessage: "Muss" })
                                                  : intl.formatMessage({ id: "match.result.ranking.optional", defaultMessage: "Kann" });
                                                const aiReasonKey = `${m.personId}:${originalIndex}`;
                                                const aiReasonOpen = aiReason ? expandedAiReasons.has(aiReasonKey) : false;
                                                return (
                                                  <div key={originalIndex} className="py-1.5 break-inside-avoid" data-testid={`candidate-requirement-${m.personId}-${unknown ? "unknown" : met ? "fulfilled" : "unfulfilled"}`}>
                                                    <div className="smycles-field-value grid min-w-0 grid-cols-[0.5rem_1.5rem_minmax(0,1fr)] items-start gap-2">
                                                      {/* Dot and lock badge are shared with the
                                                          company drill-down (CriterionDrilldown).
                                                          The LENIENCY that turns a met criterion
                                                          amber stays here: it is a person-only
                                                          state - a company criterion has no level
                                                          to be lenient about since concept v17. */}
                                                      <CriterionStatusDot
                                                        tone={
                                                          unknown
                                                            ? "attention"
                                                            : met
                                                            ? lenientMatch
                                                              ? "attention"
                                                              : "met"
                                                            : partial
                                                              ? "attention"
                                                              : "unmet"
                                                        }
                                                        title={
                                                          unknown
                                                            ? intl.formatMessage({
                                                                id: "match.result.ranking.dotUnknown",
                                                                defaultMessage: "Zu prüfen",
                                                              })
                                                            : met
                                                            ? lenientMatch
                                                              ? intl.formatMessage({
                                                                  id: "match.result.ranking.lenientMatchHint",
                                                                  defaultMessage:
                                                                    "Erfüllt per kulanter Anforderungsprüfung: Fachkompetenz vorhanden, geforderte Stufe nicht belegt.",
                                                                })
                                                              : intl.formatMessage({
                                                                  id: "match.result.ranking.dotFulfilled",
                                                                  defaultMessage: "Erfüllt",
                                                                })
                                                            : partial
                                                              ? intl.formatMessage({
                                                                  id: "match.result.ranking.dotPartial",
                                                                  defaultMessage: "Teilweise erfüllt",
                                                                })
                                                              : intl.formatMessage({
                                                                  id: "match.result.ranking.dotUnfulfilled",
                                                                  defaultMessage: "Nicht erfüllt",
                                                                })
                                                        }
                                                      />
                                                      <CriterionRequirementGlyph
                                                        isMust={isMust}
                                                        label={requirementLabel}
                                                      />
                                                      <div className="min-w-0 space-y-1.5">
                                                        <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5 text-ink">
                                                          <span className="min-w-0">{label}</span>
                                                          {constraint ? <span>({constraint})</span> : null}
                                                        </span>
                                                        {rightValue ? (
                                                          <div className="smycles-field-value text-smycles-meta leading-snug text-ink-muted">
                                                            <span className="text-ink-muted">
                                                              <FormattedMessage
                                                                id="match.result.ranking.actualInline"
                                                                defaultMessage="Nachweis:"
                                                              />{" "}
                                                            </span>
                                                            <span className="tabular-nums">{rightValue}</span>
                                                          </div>
                                                        ) : null}
                                                        {aiReason ? (
                                                          <CriterionAiReason
                                                            reason={aiReason}
                                                            open={aiReasonOpen}
                                                            onToggle={() => toggleAiReason(aiReasonKey)}
                                                          />
                                                        ) : null}
                                                      </div>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                              </div>
                                              ) : null}
                                            </div>
                                            );
                                          })
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                              </div>
                              ) : tab === "team" ? (
                                // Keine Definition an dieser Stelle: die steht am Badge-Tooltip und
                                // am Schalter im Brief. Hier nur Zahl, tragende Powers, Grundlage.
                                <div className="space-y-2 py-2">
                                  {m.teamFit == null ? <p className="smycles-field-meta"><FormattedMessage id="match.teamFit.unavailable" /></p> : (
                                    <>
                                      <p className="smycles-field-value"><FormattedMessage id="match.teamFit.contributionScore"
                                        values={{ score: intl.formatNumber(m.teamFit, { maximumFractionDigits: 1 }) }} /></p>
                                      <ul className="space-y-1" data-testid={`team-fit-elements-${m.personId}`}>
                                        {(teamFitTopById?.get(m.personId) ?? []).map(entry => (
                                          <li key={entry.element} className="smycles-field-meta flex items-baseline gap-2">
                                            <span className="text-ink">{intl.formatMessage({
                                              id: elementNameId(entry.element), defaultMessage: elementDisplayName(entry.element) })}</span>
                                            <span className="tabular-nums text-ink-muted"><FormattedMessage id="match.teamFit.elementDelta" values={{
                                              from: intl.formatNumber(entry.teamStrength, { maximumFractionDigits: 0 }),
                                              to: intl.formatNumber(entry.newStrength, { maximumFractionDigits: 0 }) }} /></span>
                                          </li>
                                        ))}
                                      </ul>
                                    </>
                                  )}
                                  <p className="smycles-field-meta text-ink-muted"><FormattedMessage id="match.teamFit.summary"
                                    values={{ count: view.recruitingTeamFit?.basis.members.length ?? 0, scorecard: view.recruitingTeamFit?.basis.scorecard.name ?? "" }} /></p>
                                  {view.recruitingTeamFit ? <p className="smycles-field-meta text-ink-muted"><FormattedMessage id="match.teamFit.measuredMembers"
                                    values={{ measured: view.recruitingTeamFit.measuredMemberCount, total: view.recruitingTeamFit.memberCount }} /></p> : null}
                                </div>
                              ) : tab === "power" ? (
                                // Nur mounten, wenn diese Zeile wirklich offen ist: der umgebende
                                // Wrapper haelt den Inhalt aller Kandidaten dauerhaft im DOM (inert +
                                // CSS-Collapse statt Unmount, s.o.), sonst wuerde buildMemberDetail()
                                // fuer jeden Kandidaten laufen, der den Power-Fit-Tab je geoeffnet hatte.
                                open ? (
                                  <PowerFitDetail
                                    person={person ?? { id: m.personId, name: m.personName }}
                                    view={view}
                                    elementWeights={elementWeights}
                                  />
                                ) : null
                              ) : aiEnabled ? (
                                <CandidateAiAssessment
                                  assessment={
                                    view.factSkill?.teamMembers.find((tm) => tm.personId === m.personId)
                                      ?.aiAssessment
                                  }
                                  stacked
                                />
                              ) : (
                                <div className="max-w-prose py-2 text-smycles-meta text-ink-muted">
                                  <FormattedMessage
                                    id="match.result.ranking.noAssessment"
                                    defaultMessage="Keine Smycles-Einschätzung vorhanden."
                                  />
                                </div>
                              )}
                              </div>
                            </div>
                      </RankingRow>
                    );
                  })}
                </ul>
            ),
          };
        })}
      />
    </div>
    </div>
  );
}

/**
 * A person in the square image field of a Top-5 card.
 *
 * Not `Avatar`: that primitive draws a fixed round rung, and this field is a square filled edge to
 * edge. It uses the same monogram style and initials helper the primitive does, so the fallback
 * stays one decision. Its company twin is `CompanyTile` in CompanyRankingPanel.
 */
function PersonTile({
  name,
  avatarContent,
  avatarMime,
}: {
  name: string;
  avatarContent?: string | null;
  avatarMime?: string | null;
}) {
  return avatarContent && avatarMime ? (
    <img
      src={`data:${avatarMime};base64,${avatarContent}`}
      alt=""
      className="absolute inset-0 h-full w-full object-cover"
    />
  ) : (
    <div
      className="absolute inset-0 flex items-center justify-center font-serif text-smycles-display-m"
      style={AVATAR_PLACEHOLDER_STYLE}
      aria-hidden="true"
    >
      {avatarInitials(name)}
    </div>
  );
}

function visibleConstraintSummary(raw: string): string {
  return raw === "all" || raw === "any" || raw === "min 1" || raw === "exact 1" ? "" : raw;
}

function criterionKindLabel(condition: unknown, intl: IntlShape): string | null {
  const id = criterionKindId(condition);
  return id ? intl.formatMessage({ id }) : null;
}

// Kandidatenweite AI-Einschätzung ("menschliche Komponente" neben dem Score): Fließtext-
// Befunde in zwei Blöcken (Positiv/Negativ) + Fazit aus dem Prüfen-Lauf, einklappbar im
// selben Aufklapp-Muster wie die Kandidatenzeile (Chevron + grid-rows-Animation),
// zugeklappt als Default. Null/leer = nicht generiert (AI aus oder Call fehlgeschlagen),
// dann erscheint die Sektion nicht.
function CandidateAiAssessment({
  assessment,
  stacked,
}: {
  assessment: AiAssessmentView | null | undefined;
  stacked?: boolean;
}) {
  const intl = useIntl();
  const [open, setOpen] = useState(true);
  if (!assessment) return null;
  const blocks = [
    {
      key: "positive",
      label: intl.formatMessage({ id: "match.result.ranking.assessment.positive", defaultMessage: "Positiv" }),
      text: assessment.positive,
    },
    {
      key: "negative",
      label: intl.formatMessage({ id: "match.result.ranking.assessment.negative", defaultMessage: "Negativ" }),
      text: assessment.negative,
    },
  ].filter((b) => b.text != null && b.text.trim().length > 0);
  const fazit = assessment.fazit != null && assessment.fazit.trim().length > 0 ? assessment.fazit : null;
  const interviewQuestions = (assessment.interviewQuestions ?? [])
    .map((question) => question.trim())
    .filter(Boolean)
    .slice(0, 3);
  if (blocks.length === 0 && !fazit && interviewQuestions.length === 0) return null;
  return (
    // Schmaler Container: unter den Kriterien mit Trennlinie oben. Breiter Container:
    // dritte Region rechts neben Anforderung/Nachweis, "Pipe"-Trenner links.
    <div
      className={
        stacked
          ? "mt-2 pb-1"
          : "mt-3 max-w-prose border-t border-ink/10 pt-2.5 @7xl:order-1 @7xl:mt-0 @7xl:min-w-0 @7xl:border-r @7xl:border-t-0 @7xl:pr-5 @7xl:pt-0"
      }
      data-testid="candidate-ai-assessment"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="group flex items-center gap-1.5 pb-1 outline-none focus-visible:ring-2 focus-visible:ring-ink-ui"
      >
        <span className="smycles-field-label font-semibold transition group-hover:text-ink-accent">
          <FormattedMessage id="match.result.ranking.assessment.title" defaultMessage="Smycles Einschätzung" />
        </span>
        <span
          aria-hidden="true"
          className={`text-smycles-label text-ink-muted transition-transform duration-[var(--duration-base)] ${
            open ? "rotate-90" : ""
          }`}
        >
          ›
        </span>
      </button>
      <div
        inert={!open}
        className={`grid transition-[grid-template-rows] duration-[var(--duration-base)] ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className={stacked ? "max-w-prose" : ""}>
            <SuitabilityBand value={assessment.suitability ?? null} />
          </div>
          {/* Flash sitzt nur auf dem Text-Block (ab Positiv), nicht auf Titel + Eignungs-Band. */}
          <div
            className={`pt-1 ${
              stacked ? "grid items-start gap-x-6 gap-y-4 @3xl:grid-cols-2 @7xl:grid-cols-4" : "space-y-2"
            }`}
          >
            {blocks.map((b) => {
              const text = b.text ?? "";
              const bulletStyle = isBulletAssessmentText(text);
              return (
                <div key={b.key} className="smycles-field-value leading-snug text-ink">
                  <span className={`font-medium text-ink ${bulletStyle ? "block" : ""}`}>
                    {b.label}:{bulletStyle ? null : " "}
                  </span>
                  {bulletStyle ? (
                    <AssessmentBulletList assessmentKey={b.key} text={text} />
                  ) : (
                    <span className="whitespace-pre-line">{text}</span>
                  )}
                </div>
              );
            })}
            {fazit ? (
              <div className={`smycles-field-value leading-snug text-ink ${stacked ? "" : "pt-1"}`}>
                <span className="font-medium">
                  <FormattedMessage id="match.result.ranking.assessment.fazit" defaultMessage="Fazit" />
                  {": "}
                </span>
                <span className="whitespace-pre-line">{fazit}</span>
              </div>
            ) : null}
            {interviewQuestions.length > 0 ? (
              <div className={stacked ? "" : "pt-2"} data-testid="candidate-interview-questions">
                <div className="smycles-field-label font-semibold text-ink-muted">
                  <FormattedMessage
                    id="match.result.ranking.assessment.interviewQuestions"
                    defaultMessage="Fragen fürs Gespräch"
                  />
                </div>
                <ol className="mt-1.5 space-y-1.5">
                  {interviewQuestions.map((question, index) => (
                    <li key={`${index}:${question}`} className="smycles-field-value flex gap-2 leading-snug text-ink">
                      <span className="shrink-0 tabular-nums text-ink-muted">{index + 1}.</span>
                      <span>{question}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function isBulletAssessmentText(text: string): boolean {
  return /^\s*(?:•|[-*])\s+/.test(text);
}

function assessmentBulletItems(text: string): string[] {
  const trimmed = text.trim();
  if (/^•/.test(trimmed)) {
    // The model occasionally returns inline or duplicated markers ("• • Text • Text").
    // In bullet mode every bullet glyph is a separator, so empty segments can be dropped safely.
    return trimmed.split(/\s*•\s*/).map((item) => item.trim()).filter(Boolean);
  }
  return trimmed
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*[-*]\s+/, "").trim())
    .filter(Boolean);
}

function AssessmentBulletList({
  assessmentKey,
  text,
}: {
  assessmentKey: string;
  text: string;
}) {
  return (
    <ul
      className="mt-1 space-y-2"
      data-testid={`candidate-ai-assessment-${assessmentKey}-bullets`}
    >
      {assessmentBulletItems(text).map((item, index) => (
        <li key={`${index}:${item}`} className="flex items-start gap-1.5">
          <span aria-hidden="true" className="shrink-0 text-ink-muted">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

// Eignungs-Band: ungeeignet (links) .. sehr geeignet (rechts). Marker sitzt bei value% (0-100).
// value == null -> nur die Skala ohne Marker (genesis liefert suitability noch nicht).
// Qualitative Einordnung passend zur konservativen genesis-Kalibrierung (90+/75+/60+/40+/<40).
function suitabilityRating(pct: number) {
  if (pct >= 90) return <FormattedMessage id="match.result.ranking.assessment.ratingVeryHigh" defaultMessage="Sehr geeignet" />;
  if (pct >= 75) return <FormattedMessage id="match.result.ranking.assessment.ratingHigh" defaultMessage="Geeignet" />;
  if (pct >= 60) return <FormattedMessage id="match.result.ranking.assessment.ratingMedium" defaultMessage="Bedingt geeignet" />;
  if (pct >= 40) return <FormattedMessage id="match.result.ranking.assessment.ratingLow" defaultMessage="Eher ungeeignet" />;
  return <FormattedMessage id="match.result.ranking.assessment.ratingVeryLow" defaultMessage="Ungeeignet" />;
}

// Kompaktes Eignungs-Mass: qualitative Bewertung + Prozent prominent, darunter eine kurze Skala
// (max-w-xs statt volle Breite). Das eingeordnete Wort liest man sofort, die Bar ist nur Kontext.
function SuitabilityBand({ value }: { value: number | null | undefined }) {
  const pct = value == null ? null : Math.max(0, Math.min(100, value));
  return (
    <div className="mb-3 max-w-xs" data-testid="suitability-band">
      {pct != null ? (
        <div className="mb-1 flex items-baseline gap-1.5">
          <span className="smycles-field-value font-medium text-ink">{suitabilityRating(pct)}</span>
          <span className="text-smycles-label tabular-nums text-ink-muted">{pct} %</span>
        </div>
      ) : null}
      <div className="relative h-3">
        <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded bg-ink/10" />
        {pct == null ? null : (
          <>
            <div
              className="absolute left-0 top-1/2 h-[3px] -translate-y-1/2 rounded"
              style={{ width: `${pct}%`, background: "var(--color-accent)" }}
            />
            <div
              className="absolute left-0 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent"
              style={{ left: `${pct}%` }}
            />
          </>
        )}
      </div>
    </div>
  );
}
